import React from 'react';
import { LockIcon, CheckIcon, ZapIcon, ShieldIcon, XIcon } from 'lucide-react';
interface ExitUpsellPopupProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}
export function ExitUpsellPopup({
  isOpen,
  onAccept,
  onDecline,
  onClose
}: ExitUpsellPopupProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-444">
      <div className="bg-dark-900 border-2 border-cyan-500/50 rounded-2xl max-w-2xl w-full overflow-hidden shadow-[0_0_50px_rgba(0,240,255,0.2)] relative flex flex-col max-h-[95vh]" data-id="element-445">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white z-10 transition-colors" aria-label="Close popup" data-id="element-446">
          <XIcon className="w-6 h-6" data-id="element-447" />
        </button>

        {/* Header Banner */}
        <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border-b border-red-500/30 p-6 text-center relative overflow-hidden shrink-0" data-id="element-448">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-orange-500" data-id="element-449"></div>
          <span className="inline-block px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold uppercase tracking-widest rounded-full mb-3 border border-red-500/30" data-id="element-450">
            Wait! Don't Leave Money On The Table
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-451">
            🛑 Stop! Upgrade Your Engine Before You Checkout
          </h2>
          <p className="text-gray-300 text-lg" data-id="element-452">
            Get the complete Architect Infrastructure at a fraction of the cost.
          </p>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8 overflow-y-auto" data-id="element-453">
          <div className="flex flex-col gap-4 items-center mb-6 text-center" data-id="element-454">
            <div className="inline-flex items-center justify-center px-4 py-2 bg-amber-500/20 border border-amber-500/50 text-amber-400 font-bold rounded-lg mb-2 animate-pulse" data-id="element-455">
              🔥 30% OFF ALL UPGRADES
            </div>
            <p className="text-gray-300 leading-relaxed max-w-lg" data-id="element-456">
              As an Architect, your time is your most valuable asset. Don't
              waste your weekend building systems from scratch or sending
              proposals that look like resumes.
            </p>
            <p className="text-cyan-400 font-medium max-w-lg" data-id="element-457">
              Add the complete Zero-Draft Library and Authority Shield to your
              order right now and save 30% instantly.
            </p>
          </div>

          {/* What's Included */}
          <div className="bg-dark-950 rounded-xl border border-cyan-900/30 p-5 mb-8" data-id="element-458">
            <h3 className="text-white font-bold mb-4 uppercase tracking-wider text-sm" data-id="element-459">
              What's Included in the Complete Package:
            </h3>
            <ul className="space-y-3" data-id="element-460">
              <li className="flex items-start gap-3" data-id="element-461">
                <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-462" />
                <span className="text-gray-300" data-id="element-463">
                  <strong className="text-white" data-id="element-464">
                    The $10K AI Content Engine
                  </strong>
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-465">
                <CheckIcon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" data-id="element-466" />
                <span className="text-gray-300" data-id="element-467">
                  <strong className="text-white" data-id="element-468">All 3 Free Bonuses</strong>{' '}
                  (Offer Clarity Kit, Scripts, Execution Planner)
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-469">
                <ZapIcon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" data-id="element-470" />
                <span className="text-gray-300" data-id="element-471">
                  <strong className="text-white" data-id="element-472">
                    The Zero-Draft Library:
                  </strong>{' '}
                  Mental + technical architecture for publish-ready drafts
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-473">
                <ShieldIcon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-474" />
                <span className="text-gray-300" data-id="element-475">
                  <strong className="text-white" data-id="element-476">The Authority Shield:</strong>{' '}
                  1-Page Service Agreement to close $2k-$5k retainers
                </span>
              </li>
            </ul>
          </div>

          {/* Pricing & CTAs */}
          <div className="text-center" data-id="element-477">
            <div className="flex items-center justify-center gap-3 mb-2" data-id="element-478">
              <span className="text-2xl text-gray-500 line-through decoration-red-500/50" data-id="element-479">
                $45.00
              </span>
              <span className="text-5xl font-bold text-cyan-400 text-glow" data-id="element-480">
                $31.50
              </span>
            </div>
            <p className="text-sm text-amber-400 mb-6 font-medium" data-id="element-481">
              Special price only available on this screen
            </p>

            <button onClick={onAccept} className="w-full group relative inline-flex items-center justify-center px-8 py-5 font-bold text-black bg-gradient-to-r from-cyan-400 to-cyan-300 rounded-lg overflow-hidden transition-all hover:scale-[1.02] shadow-[0_0_30px_rgba(0,240,255,0.4)] mb-4 animate-pulse hover:animate-none" data-id="element-482">
              <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-white rounded-full group-hover:w-full group-hover:h-56 opacity-20" data-id="element-483"></span>
              <span className="relative text-lg tracking-wide uppercase flex items-center gap-2" data-id="element-484">
                YES! I want the Complete Package for $31.50
              </span>
            </button>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-6" data-id="element-485">
              <LockIcon className="w-4 h-4 text-green-400" data-id="element-486" />
              <span data-id="element-487">Secure 1-Click Upgrade</span>
            </div>

            <button onClick={onDecline} className="text-gray-500 hover:text-gray-300 underline transition-colors text-sm" data-id="element-488">
              No thanks, continue without the extras →
            </button>
          </div>
        </div>
      </div>
    </div>;
}